import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { useLang } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { lang } = useLang();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 20) {
        if (!cancelled) setStatus("expired");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          const amountEUR = (data.amount_total || 0) / 100;
          const kind = data.kind || "unknown";
          // GA4 standard "purchase" event for revenue tracking
          trackEvent("purchase", {
            transaction_id: sessionId,
            value: amountEUR,
            currency: (data.currency || "eur").toUpperCase(),
            payment_kind: kind,
          });
          // Custom event for funnel segmentation
          trackEvent(`payment_success_${kind}`, {
            value: amountEUR,
            currency: (data.currency || "eur").toUpperCase(),
          });
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        // ignore transient errors
      }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="payment-success-page">
      <Card>
        <CardContent className="p-10 space-y-4">
          {status === "polling" && (
            <>
              <Clock className="w-12 h-12 text-primary mx-auto animate-pulse" />
              <h1 className="font-display font-bold text-2xl">{lang === "fr" ? "Vérification du paiement…" : "Verifying payment…"}</h1>
              <p className="text-sm text-muted-foreground">{lang === "fr" ? "Merci de patienter quelques secondes." : "Please wait a few seconds."}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-accent mx-auto" data-testid="paid-icon" />
              <h1 className="font-display font-bold text-3xl">
                {lang === "fr" ? "Paiement confirmé !" : "Payment confirmed!"}
              </h1>
              {details?.kind === "application_fee" && (
                <p className="text-sm text-muted-foreground" data-testid="paid-kind-application-fee">
                  {lang === "fr"
                    ? `Frais de dossier réglés (${(details?.amount_total / 100).toLocaleString("fr-FR")} €). Votre candidature est désormais transmise à notre équipe d'admissions.`
                    : `Application fee paid (€${(details?.amount_total / 100).toLocaleString("en-US")}). Your application is now forwarded to our admissions team.`}
                </p>
              )}
              {details?.kind === "enrollment_deposit" && (
                <p className="text-sm text-muted-foreground" data-testid="paid-kind-enrollment-deposit">
                  {lang === "fr"
                    ? `Acompte d'inscription reçu (${(details?.amount_total / 100).toLocaleString("fr-FR")} €). Votre place est confirmée — le solde sera prélevé selon le plan choisi.`
                    : `Enrollment deposit received (€${(details?.amount_total / 100).toLocaleString("en-US")}). Your seat is confirmed — the balance will be charged per your plan.`}
                </p>
              )}
              {details?.kind === "us_history_deposit" && (
                <p className="text-sm text-muted-foreground" data-testid="paid-kind-us-history">
                  {details?.waitlisted
                    ? (lang === "fr"
                        ? `Acompte US History reçu (95 €). Le cours est complet pour cette année — votre enfant est ajouté à la liste d'attente. L'équipe vous tiendra informé dès qu'une place se libère.`
                        : `US History deposit received (€95). The class is full for this year — your child has been added to the waiting list. Our team will notify you as soon as a seat opens.`)
                    : (lang === "fr"
                        ? `Acompte US History reçu (95 €). Le cours est désormais ajouté à l'emploi du temps de l'élève.`
                        : `US History deposit received (€95). The course is now added to the student's schedule.`)}
                </p>
              )}
              {(!details?.kind || details?.kind === "balance_installment") && (
                <p className="text-sm text-muted-foreground">
                  {lang === "fr"
                    ? `Nous avons reçu ${(details?.amount_total / 100).toLocaleString("fr-FR")} ${details?.currency?.toUpperCase()}. Bienvenue à OFA France.`
                    : `We received ${(details?.amount_total / 100).toLocaleString("en-US")} ${details?.currency?.toUpperCase()}. Welcome to OFA France.`}
                </p>
              )}
              {(details?.kind === "enrollment_deposit" || details?.kind === "us_history_deposit")
                && details?.enrollment_id ? (
                <Link to={`/parent/availability/${details.enrollment_id}`}>
                  <Button data-testid="go-to-availability" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6">
                    {lang === "fr" ? "Renseigner les disponibilités →" : "Set availability →"}
                  </Button>
                </Link>
              ) : (
                <Link to="/parent"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">{lang === "fr" ? "Retour au tableau de bord" : "Back to dashboard"}</Button></Link>
              )}
            </>
          )}
          {(status === "expired" || status === "error") && (
            <>
              <XCircle className="w-12 h-12 text-destructive mx-auto" />
              <h1 className="font-display font-bold text-2xl">{lang === "fr" ? "Paiement non confirmé" : "Payment not confirmed"}</h1>
              <p className="text-sm text-muted-foreground">{lang === "fr" ? "Le paiement n'a pas été enregistré. Vous pouvez réessayer depuis votre tableau de bord." : "Payment was not recorded. You can retry from your dashboard."}</p>
              <Link to="/parent"><Button variant="outline" className="rounded-full px-6">{lang === "fr" ? "Tableau de bord" : "Dashboard"}</Button></Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
